import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { productsAPI } from '../api/products';
import { categoriesAPI } from '../api/categories';
import { useCart } from '../context/CartContext';
import { ProductDetail, Category } from '../types/product';
import CCOptionsEditModal from '../components/Cart/CCOptionsEditModal';
import { getLocalizedField } from '../utils/localize';
import type { SupportedLanguage } from '../i18n';

// ─── Colour tokens (Variation B — warm cream / rose accent) ──────────────────

const CC_PAGE_BG   = '#FAF8F3'; // warm cream background
const CC_CARD_BG   = '#F5F0E8'; // light cream product cards
const CC_ACCENT    = '#C96D6D'; // rose/warm red CTA
const CC_ACCENT_HV = '#B05C5C'; // darker rose on hover
const CC_TEXT_PRI  = '#1C1511'; // primary text
const CC_TEXT_MUT  = '#6B5E4E'; // muted text
const CC_BORDER    = '#E8DDD0'; // border

// ─── Styled components ────────────────────────────────────────────────────────

const PageContainer = styled.div`
  min-height: 100vh;
  padding-top: calc(var(--header-height, 70px) + 1rem);
  padding-bottom: 3rem;
  background-color: ${CC_PAGE_BG};
  color: ${CC_TEXT_PRI};
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const PageTitle = styled.h1`
  color: ${CC_TEXT_PRI};
  margin-bottom: 0.5rem;
  font-size: 2rem;
  font-family: Georgia, 'Times New Roman', serif;
`;

const PageSubtitle = styled.p`
  color: ${CC_TEXT_MUT};
  margin-bottom: 2rem;
`;

// ─── Category bar ─────────────────────────────────────────────────────────────

const CategoryBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-bottom: 2rem;
  position: sticky;
  top: var(--header-height, 70px);
  z-index: 10;
  background-color: ${CC_PAGE_BG};
  padding: 0.75rem 0;
  border-bottom: 1px solid ${CC_BORDER};

  @media (max-width: 767px) {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const CategoryButton = styled.button`
  padding: 0.45rem 1.1rem;
  min-height: 44px;
  border-radius: 20px;
  border: 2px solid ${CC_ACCENT};
  background-color: transparent;
  color: ${CC_ACCENT};
  font-size: clamp(0.75rem, 2vw, 0.9rem);
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;
  white-space: nowrap;
  flex-shrink: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 240px;

  @media (max-width: 767px) {
    max-width: 45vw;
  }

  &:hover {
    background-color: ${CC_ACCENT_HV};
    border-color: ${CC_ACCENT_HV};
    color: #ffffff;
  }
`;

// ─── Product grid ─────────────────────────────────────────────────────────────

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 540px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 1100px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const ProductCard = styled.div`
  background: ${CC_CARD_BG};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
  display: flex;
  flex-direction: column;
  border: 1px solid ${CC_BORDER};
  transition: transform 0.15s, box-shadow 0.15s;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(201, 109, 109, 0.18);
  }
`;

const ProductCardImage = styled.div<{ $src?: string | null }>`
  width: 100%;
  height: 170px;
  background-color: ${CC_BORDER};
  background-image: ${props => props.$src ? `url(${props.$src})` : 'none'};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
`;

const ProductCardBody = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ProductCardName = styled.h3`
  margin: 0 0 0.5rem;
  font-size: 1rem;
  color: ${CC_TEXT_PRI};
`;

const ProductCardPrice = styled.span`
  font-weight: 700;
  font-size: 1.05rem;
  color: ${CC_ACCENT};
  margin-bottom: 0.75rem;
  display: block;
`;

const AddToCartButton = styled.button`
  margin-top: auto;
  padding: 0.5rem 1rem;
  background-color: ${CC_ACCENT};
  color: #ffffff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: background-color 0.15s;

  &:hover {
    background-color: ${CC_ACCENT_HV};
  }
`;

// ─── BYO entry card ───────────────────────────────────────────────────────────

const BYOCard = styled.div`
  background: ${CC_CARD_BG};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
  display: flex;
  flex-direction: row;
  align-items: center;
  border: 2px solid ${CC_ACCENT};
  margin-bottom: 2rem;
  padding: 1.5rem;
  gap: 1.5rem;
  transition: transform 0.15s, box-shadow 0.15s;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(201, 109, 109, 0.18);
  }
`;

const BYOIconArea = styled.div`
  width: 80px;
  height: 80px;
  background-color: ${CC_BORDER};
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  flex-shrink: 0;
`;

const BYOCardBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0.4rem;
`;

const BYOCardName = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  color: ${CC_TEXT_PRI};
  font-family: Georgia, 'Times New Roman', serif;
`;

const BYOCardSubtitle = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: ${CC_TEXT_MUT};
`;

const BYOStartButton = styled.button`
  align-self: flex-start;
  padding: 0.55rem 1.4rem;
  background-color: ${CC_ACCENT};
  color: #ffffff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 700;
  margin-top: 0.5rem;
  transition: background-color 0.15s;

  &:hover {
    background-color: ${CC_ACCENT_HV};
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const CustomCakesMenu: React.FC = () => {
  const { t, i18n } = useTranslation();
  // Mirrors the LanguageToggle.tsx / ProductDetail.tsx pattern: i18next's
  // language can be a region-qualified tag or unset, so narrow to the two
  // v1-supported values.
  const language: SupportedLanguage = (i18n.language as SupportedLanguage) === 'zh' ? 'zh' : 'en';

  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [addingProducts, setAddingProducts] = useState<Set<number>>(new Set());
  const [sizePickerProduct, setSizePickerProduct] = useState<ProductDetail | null>(null);

  const handleAddToCart = (e: React.MouseEvent<HTMLButtonElement>, product: ProductDetail) => {
    e.stopPropagation();

    // Prevent double clicks
    if (addingProducts.has(product.product_id)) {
      return;
    }

    // Products with a REQUIRED option group must have a selection made
    // before being added to cart (the server rejects the line otherwise).
    // Quick-add is only for products with no required group.
    if (product.has_required_options) {
      setSizePickerProduct(product);
      return;
    }

    setAddingProducts(prev => new Set(prev).add(product.product_id));
    addToCart(product, 1);

    // Reset the flag after a short delay
    setTimeout(() => {
      setAddingProducts(prev => {
        const next = new Set(prev);
        next.delete(product.product_id);
        return next;
      });
    }, 500);
  };

  const { data: productsData, isLoading: productsLoading, error: productsError } = useQuery({
    queryKey: ['ccProducts'],
    queryFn: () => productsAPI.getProducts(),
  });

  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ['ccCategories'],
    queryFn: () => categoriesAPI.getCategories(),
  });

  const products: ProductDetail[] = Array.isArray(productsData) ? productsData : [];
  const categories: Category[] = Array.isArray(categoriesData) ? categoriesData : [];

  const formatPrice = (price: number) => `£${price.toFixed(2)}`;

  const getPrimaryImage = (product: ProductDetail): string | null => {
    if (!Array.isArray(product.images) || product.images.length === 0) return null;
    const primary = product.images.find(img => img.is_primary);
    return primary ? primary.image_url : product.images[0].image_url;
  };

  const isLoading = productsLoading || categoriesLoading;

  const toSlug = (name: string) => name.toLowerCase().replace(/\s+/g, '-');

  const ccGrouped = useMemo(() => {
    const map = new Map<any, any[]>();
    (categories || []).forEach((cat: any) => {
      const items = (products || []).filter((p: any) => p.category_id === cat.category_id);
      if (items.length > 0) map.set(cat, items);
    });
    return map;
  }, [categories, products]);

  if (isLoading) {
    return (
      <PageContainer>
        <ContentWrapper>
          <PageTitle>{t('customCakesMenu.pageTitle')}</PageTitle>
          <p>{t('customCakesMenu.loadingMenu')}</p>
        </ContentWrapper>
      </PageContainer>
    );
  }

  if (productsError) {
    return (
      <PageContainer>
        <ContentWrapper>
          <PageTitle>{t('customCakesMenu.pageTitle')}</PageTitle>
          <p style={{ color: '#e53e3e' }}>{t('customCakesMenu.loadError')}</p>
        </ContentWrapper>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentWrapper>
        <PageTitle>{t('customCakesMenu.pageTitle')}</PageTitle>
        <PageSubtitle>{t('customCakesMenu.subtitle')}</PageSubtitle>

        <BYOCard data-testid="byo-entry-card">
          <BYOIconArea>🎂</BYOIconArea>
          <BYOCardBody>
            <BYOCardName>{t('customCakesMenu.byoTitle')}</BYOCardName>
            <BYOCardSubtitle>{t('customCakesMenu.byoSubtitle')}</BYOCardSubtitle>
            <BYOStartButton onClick={() => navigate('/custom-cakes/build-your-own')}>
              {t('customCakesMenu.byoStartButton')}
            </BYOStartButton>
          </BYOCardBody>
        </BYOCard>

        {ccGrouped.size > 0 && (
          <CategoryBar>
            {Array.from(ccGrouped.keys()).map((cat: any) => (
              <CategoryButton
                key={cat.category_id}
                onClick={() =>
                  document.getElementById(`cc-cat-${toSlug(cat.name)}`)
                    ?.scrollIntoView({ behavior: 'smooth' })
                }
              >
                {cat.name}
              </CategoryButton>
            ))}
          </CategoryBar>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
          {ccGrouped.size === 0 ? (
            <p style={{ color: 'var(--text-color)', opacity: 0.5, textAlign: 'center' }}>
              {t('customCakesMenu.noProductsAvailable')}
            </p>
          ) : (
            Array.from(ccGrouped.entries()).map(([cat, items]) => (
              <div
                key={`cc-cat-${cat.category_id}`}
                id={`cc-cat-${toSlug(cat.name)}`}
                style={{ marginBottom: '2rem' }}
              >
                <h3 style={{
                  color: CC_TEXT_PRI,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  marginBottom: '1rem',
                }}>
                  {cat.name}
                </h3>
                <ProductGrid>
                  {items.map((product: any) => {
                    const imgSrc = getPrimaryImage(product);
                    return (
                      <ProductCard key={product.product_id} onClick={() => navigate(`/custom-cakes/${product.product_id}`)}>
                        <ProductCardImage $src={imgSrc}>
                          {!imgSrc && '🎂'}
                        </ProductCardImage>
                        <ProductCardBody>
                          <ProductCardName>{getLocalizedField(product, 'name', language) as string}</ProductCardName>
                          <ProductCardPrice>{formatPrice(product.base_price)}</ProductCardPrice>
                          <AddToCartButton
                            onClick={(e) => handleAddToCart(e, product)}
                            disabled={addingProducts.has(product.product_id)}
                          >
                            {addingProducts.has(product.product_id) ? t('customCakesMenu.adding') : t('customCakesMenu.addToCart')}
                          </AddToCartButton>
                        </ProductCardBody>
                      </ProductCard>
                    );
                  })}
                </ProductGrid>
              </div>
            ))
          )}
        </div>
      </ContentWrapper>

      {sizePickerProduct && (
        <CCOptionsEditModal
          item={{ product: sizePickerProduct, quantity: 1 }}
          onConfirm={(ccOptions, price) => {
            addToCart(sizePickerProduct, 1, undefined, price, undefined, undefined, ccOptions);
            setSizePickerProduct(null);
          }}
          onClose={() => setSizePickerProduct(null)}
        />
      )}
    </PageContainer>
  );
};

export default CustomCakesMenu;
